import { ChatBubble } from './ChatBubble.js';
import { evaluateTick } from './routeRules.mjs';

// The bridge between the pure task machine (routeRules.mjs) and everything the child can
// see on a training route: it collects the car's state and the scripted world's into one
// `input` per frame, hands it to `evaluateTick`, and turns the events that come back into
// the instructor's strip, a bubble over the car, a light going green, a kid stepping onto
// the zebra, a car pulling out of a side road — and, at the end, one call to `onFinish`.
//
//   const director = new RouteDirector(scene, { run, car, strip, actors, onFinish });
//   // every frame, AFTER car.update(dt):
//   director.update(dt);
//
// `actors` is a Map from TASK id to the one thing that task animates: a TrafficActor for
// `stop`/`yield` (the side road), `solid` (the car parked in your lane) and `oncoming`, a
// PedestrianActor for `crossing`, a TrafficLight for `light`. The scene builds them (it owns
// the world); the director only ever wakes them and reads their state.

const FINISH_DELAY_MS = 1000;    // the car rolls to a stop before the result is sent
const CRASH_LOCK_MS = 1000;      // …and the wheel goes dead for a second after a bump
const BUBBLE_MS = 3500;          // one bubble at a time, above the car
const LIGHT_RED_MS = 4000;       // red → red+yellow → green, driven by `trigger-light`
const LIGHT_REDYELLOW_MS = 1500;
// Two cars whose wheels are within this many px of the same ground line are in the same
// lane. Cars are ~190 px of image tall and the lanes only 40 px apart, so a full-height
// AABB would have everything hit everything — the footprint is what collides.
const LANE_BAND = 26;

// The instructor's coaching lines, one per `need` the rules can raise. Distinct from
// TASK_COPY_PL's `hint` (which teaches the rule before you get there): these are said while
// you are IN the situation and haven't done the thing yet.
export const NEED_COPY_PL = {
  stop: 'Zatrzymaj się przed linią!',
  'wait-side': 'Poczekaj, aż tamto auto przejedzie…',
  'wait-pedestrian': 'Zatrzymaj się i przepuść pieszego!',
  'wait-light': 'Czekamy na zielone…',
  follow: 'Zatrzymaj się całkiem za autem — po chwili ruszy',
  'lane-back': 'Wróć na swój pas!',
  park: 'Wjedź całym autem między linie zatoki.',
  'park-lane': 'Zjedź {arrow_down} w dół, aż staniesz w zatoce — parkujemy w niej, nie na drodze.',
};

const isResolved = (task) => task.status === 'ok' || task.status === 'fail';
const overlaps = (a, b) => a.left < b.right && b.left < a.right
  && Math.abs(a.groundY - b.groundY) <= LANE_BAND;

export class RouteDirector {
  constructor(scene, { run, car, strip = null, actors = new Map(), onFinish = null } = {}) {
    this.scene = scene;
    this.run = run;
    this.car = car;
    this.strip = strip;
    this.actors = actors;
    this._onFinish = onFinish;

    this._bubble = null;
    this._bubbleTimer = null;
    this._finishing = false;
    this._crashed = false;        // a collision happened on THIS frame
    this._oncomingHit = false;    // …and the one that counts as the oncoming task's mistake
    this._touching = new Set();   // actors we are already inside — one crash per bump
    this._lastNeed = null;        // `need` fires every frame; the strip only re-renders on a change
    this._lastDistance = null;
    this._lightTimers = [];
    this._limit = null;           // what the HUD's sign currently reads
  }

  /**
   * One frame. Call AFTER `car.update(dt)`: `laneChangedThisFrame` belongs to the update
   * that just ran, and the rules have to see the car's new x.
   */
  update(deltaMs) {
    if (this._finishing || this.run.finished) return;
    if (!this.car?.isDriving) return;
    const dt = Math.min(deltaMs, 100) / 1000;

    const carFront = this._carBounds().left;   // driving left, the bonnet is the low-x edge
    for (const actor of this.actors.values()) actor.update?.(dt, this.car.sprite.x, this.car.lane, carFront);
    this._checkCollisions();
    this._syncLimitSign();

    const events = evaluateTick(this.run, {
      x: this.car.sprite.x,
      v: this.car.drive.v,
      lane: this.car.lane,
      laneChanging: this.car.laneChanging,
      laneChangedThisFrame: this.car.laneChangedThisFrame,
      facing: this.car.drive.facing < 0 ? 'left' : 'right',
      dt,
      carWidth: this.car.sprite.img?.displayWidth ?? 0,
      actors: this._actorState(),
    });
    for (const ev of events) this._handle(ev);
  }

  // --- what the world looks like this frame ----------------------------------------------

  _firstUnresolved(...kinds) {
    return this.run.tasks.find((t) => kinds.includes(t.kind) && !isResolved(t)) ?? null;
  }

  // The rules take ONE world state, not one per task, so each field comes from the actor of
  // the nearest task that still cares about it — which is the one being driven into.
  _actorState() {
    const state = {
      sideRoadPassed: false, pedestrian: 'none', light: 'off',
      parkedCarAhead: false, parkedCarGone: false, parkedCarX: null,
      oncomingHit: this._oncomingHit, crashed: this._crashed,
    };
    // The car that pulled away in front of us: the overtake rule wants to know where it is
    // long after the `solid` task itself has resolved.
    for (const actor of this.actors.values()) {
      if (actor.kind === 'parked' && actor.active) state.parkedCarX = actor.x;
    }
    const junction = this._firstUnresolved('stop', 'yield');
    if (junction) state.sideRoadPassed = !!this.actors.get(junction.id)?.passed;

    const crossing = this._firstUnresolved('crossing');
    if (crossing) state.pedestrian = this.actors.get(crossing.id)?.state ?? 'none';

    const light = this._firstUnresolved('light');
    if (light) state.light = this.actors.get(light.id)?.state ?? 'off';

    const solid = this._firstUnresolved('solid');
    const parked = solid ? this.actors.get(solid.id) : null;
    if (parked) {
      state.parkedCarAhead = parked.aheadOf(this.car.sprite.x);
      state.parkedCarGone = parked.gone;
    }
    return state;
  }

  // The car's own footprint: its body's x span on the ground line its wheels stand on.
  _carBounds() {
    const w = (this.car.sprite.img?.displayWidth ?? 200) * 0.9;
    return {
      left: this.car.sprite.x - w / 2,
      right: this.car.sprite.x + w / 2,
      groundY: this.car.sprite.groundY ?? 0,
    };
  }

  // One crash per bump: an actor we are already inside doesn't crash us again every frame.
  // Clipping the ONCOMING car is that task's own mistake rather than a generic crash, so it
  // isn't counted twice.
  _checkCollisions() {
    this._crashed = false;
    // A car standing still crashes into nobody: the child walking across in front of a
    // stopped bonnet is the crossing working, not a bump. If it drives off into her while
    // they overlap, the very next moving frame says so.
    if (this.car.drive.v === 0) return;
    const mine = this._carBounds();
    for (const [id, actor] of this.actors) {
      const theirs = actor.bounds?.();
      if (!theirs || !overlaps(mine, theirs)) { this._touching.delete(id); continue; }
      if (this._touching.has(id)) continue;
      this._touching.add(id);
      actor.jumpBack?.();
      if (actor.kind === 'oncoming') { this._oncomingHit = true; continue; }
      this._crashed = true;
      this._lockAfterCrash();
    }
  }

  _lockAfterCrash() {
    this.car.lockInput();
    this.scene.time.delayedCall(CRASH_LOCK_MS, () => {
      if (!this._finishing) this.car?.unlockInput();
    });
  }

  // The HUD's sign reads whatever limit is in force where the car is, and nothing at all
  // between them — a route has no default limit of its own. Status plays no part: a sign
  // you have already been caught speeding under is still the sign standing over this road,
  // and blanking the HUD on the first ❌ took the number away exactly when it was needed.
  // Bound is the sign post itself (`meta.signX`, falling back to `t.x0` if a task somehow
  // lacks it) rather than the announce point 400 px earlier — the HUD should show the number
  // from where the sign actually stands, not from where the strip starts talking about it.
  _syncLimitSign() {
    const x = this.car.sprite.x;
    const task = this.run.tasks.find(
      (t) => t.kind === 'limit' && x <= (t.meta.signX ?? t.x0) && x >= t.x1,
    );
    const limit = task ? task.meta.limit : null;
    if (limit === this._limit) return;
    this._limit = limit;
    this.car.setSpeedLimit(limit);
  }

  // --- events -----------------------------------------------------------------------------

  _handle(ev) {
    switch (ev.type) {
      case 'announce': return this._onAnnounce(ev);
      case 'progress': return this._onProgress(ev);
      case 'activate': return this._onActivate(ev);
      case 'need': return this._onNeed(ev);
      case 'ok': return this._onOk(ev);
      case 'fail': return this._onFail(ev);
      case 'trigger-light': return this._startLight(ev.task);
      case 'trigger-side': return this._wake(ev.task);
      // Each oncoming car is its own task: clear the hit flag as this one is woken, or a
      // clip earlier in the route would still be reading true when it is judged.
      case 'trigger-oncoming': this._oncomingHit = false; return this._wake(ev.task);
      case 'parked-go': return this._wake(ev.task);
      case 'pedestrian-go': return this.actors.get(ev.task.id)?.go?.();
      case 'finish': return this._finish(ev);
      default: return undefined;
    }
  }

  _wake(task) {
    this.actors.get(task.id)?.start?.();
  }

  _onAnnounce(ev) {
    this._lastNeed = null;
    this._lastDistance = ev.distanceM;
    this.strip?.announce(ev.task, ev.distanceM);
    // Lesson 1 only (buildTasks nulls the hint elsewhere): the rule, said out loud, above
    // the car — the strip keeps the countdown while she explains.
    if (ev.hint) this._say(ev.hint);
  }

  _onProgress(ev) {
    // The strip is a DOM node: only touch it when the metre reading actually changes.
    if (ev.distanceM === this._lastDistance) return;
    this._lastDistance = ev.distanceM;
    if (this._lastNeed) return;      // …and never over a coaching line
    this.strip?.progress(ev.distanceM);
  }

  _onActivate(ev) {
    this._lastNeed = null;
    this._lastDistance = null;
    this.strip?.activate(ev.task);
  }

  // `need` repeats every frame for as long as the situation lasts — render it once.
  _onNeed(ev) {
    if (this._lastNeed === ev.what) return;
    this._lastNeed = ev.what;
    const text = NEED_COPY_PL[ev.what];
    if (text) this.strip?.coach(text);
  }

  _onOk(ev) {
    this._lastNeed = null;
    this._lastDistance = null;
    // `lane` and `noentry` have nothing to celebrate — the strip just goes back to resting.
    if (!ev.text) { this.strip?.idle(); return; }
    this.strip?.ok(ev.text);
    this._say('{check}');
  }

  _onFail(ev) {
    this._lastNeed = null;
    this._lastDistance = null;
    // A crossing driven through: the child on the zebra gets out of the way.
    if (ev.task.kind === 'crossing') this.actors.get(ev.task.id)?.jumpBack?.();
    const text = ev.text ?? 'Ojej…';
    this.strip?.fail(text);
    this._say(`{cross} ${text}`);
  }

  _startLight(task) {
    const light = this.actors.get(task.id);
    if (!light) return;
    light.set('red');
    this._lightTimers.push(this.scene.time.delayedCall(LIGHT_RED_MS, () => {
      light.set('redyellow');
      this._lightTimers.push(this.scene.time.delayedCall(LIGHT_REDYELLOW_MS, () => light.set('green')));
    }));
  }

  // --- bubbles ----------------------------------------------------------------------------

  _say(text) {
    this._clearBubble();
    if (!this.car?.sprite) return;
    this._bubble = new ChatBubble(this.scene, this.car.sprite, text);
    this._bubbleTimer = this.scene.time.delayedCall(BUBBLE_MS, () => this._clearBubble());
  }

  _clearBubble() {
    this._bubbleTimer?.remove?.();
    this._bubbleTimer = null;
    this._bubble?.destroy();
    this._bubble = null;
  }

  // --- the end ----------------------------------------------------------------------------

  // The route is over: the wheel goes dead (the car brakes itself to a stop — see
  // CarController.lockInput) and a second later the scene sends the results.
  _finish(ev) {
    if (this._finishing) return;
    this._finishing = true;
    // The strip keeps whatever it last said — an exam that ends on a ❌ has to leave the
    // reason on screen, not blank it out on the very frame it happened.
    this.car?.lockInput();
    this.scene.time.delayedCall(FINISH_DELAY_MS, () => {
      this._onFinish?.({ failed: !!ev.failed, run: this.run });
    });
  }

  destroy() {
    this._clearBubble();
    for (const timer of this._lightTimers) timer?.remove?.();
    this._lightTimers = [];
    for (const actor of this.actors.values()) actor.destroy?.();
    this.actors = new Map();
    this.car = null;
    this.strip = null;
    this._onFinish = null;
  }
}
